import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  History, User, FileText, Settings, Users, LogIn, LogOut,
  RefreshCw, ChevronRight, Download, Eye, Edit, Trash2,
  Filter, Clock, Activity
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AuditLogViewer() {
  const [activity, setActivity] = useState(null);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('activity');
  const [filters, setFilters] = useState({
    resource_type: '',
    action: ''
  });
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab, filters, offset]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const activityRes = await axios.get(`${API}/audit/activity?limit=30`, { withCredentials: true });
      setActivity(activityRes.data);
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '20');
      params.append('offset', offset.toString());
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      if (filters.action) params.append('action', filters.action);
      
      const logsRes = await axios.get(`${API}/audit/logs?${params.toString()}`, { withCredentials: true });
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  };

  const getActionIcon = (action, resourceType) => {
    if (action === 'create') return <FileText className="w-4 h-4 text-emerald-500" />;
    if (action === 'update') return <Edit className="w-4 h-4 text-blue-500" />;
    if (action === 'delete') return <Trash2 className="w-4 h-4 text-rose-500" />;
    if (action === 'export') return <Download className="w-4 h-4 text-purple-500" />;
    if (action === 'view') return <Eye className="w-4 h-4 text-slate-500" />;
    if (action === 'login') return <LogIn className="w-4 h-4 text-emerald-500" />;
    if (action === 'logout') return <LogOut className="w-4 h-4 text-amber-500" />;
    if (resourceType === 'team') return <Users className="w-4 h-4 text-indigo-500" />;
    if (resourceType === 'settings') return <Settings className="w-4 h-4 text-slate-500" />;
    return <Activity className="w-4 h-4 text-slate-400" />;
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'update': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'delete': return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';
      case 'export': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'login': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'logout': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      default: return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
        {[
          { id: 'activity', label: 'Activity Feed', icon: Activity },
          { id: 'logs', label: 'Audit Logs', icon: History }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2 ${
              activeTab === tab.id
                ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
            }`}
            data-testid={`audit-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Activity Feed Tab */}
      {activeTab === 'activity' && activity && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 dark:text-white">Recent Activity</h3>
              <button 
                onClick={fetchData}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            
            {activity.activity?.length > 0 ? (
              <div className="space-y-1">
                {activity.activity.map((item, idx) => (
                  <div 
                    key={item.id || idx}
                    className="flex items-start gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                      {getActionIcon(item.action?.split(' ')[0], item.resource_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900 dark:text-white">
                        <span className="font-medium">{item.user}</span>
                        {' '}
                        <span className="text-slate-600 dark:text-slate-400">{item.action}</span>
                      </p>
                      {item.resource_id && (
                        <p className="text-xs text-slate-500 truncate mt-0.5">
                          ID: {item.resource_id}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-slate-400 whitespace-nowrap">
                      {item.relative_time}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No recent activity</p>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Audit Logs Tab */}
      {activeTab === 'logs' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Filters */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Filters</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select
                value={filters.resource_type}
                onChange={(e) => {
                  setFilters({ ...filters, resource_type: e.target.value });
                  setOffset(0);
                }}
                className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All Resources</option>
                <option value="invoice">Invoices</option>
                <option value="user">Users</option>
                <option value="team">Teams</option>
                <option value="settings">Settings</option>
                <option value="comment">Comments</option>
              </select>
              <select
                value={filters.action}
                onChange={(e) => {
                  setFilters({ ...filters, action: e.target.value });
                  setOffset(0);
                }}
                className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="export">Export</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
              </select>
            </div>
          </div>

          {/* Logs List */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
            {logs?.logs?.length > 0 ? (
              <>
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {logs.logs.map((log, idx) => (
                    <div 
                      key={log.log_id || idx}
                      className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                            {getActionIcon(log.action, log.resource_type)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getActionColor(log.action)}`}>
                                {log.action}
                              </span>
                              <span className="px-2 py-0.5 text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded">
                                {log.resource_type}
                              </span>
                            </div>
                            <p className="text-sm text-slate-900 dark:text-white mt-1">
                              {log.user_name || log.user_email}
                            </p>
                            {log.resource_id && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                Resource: {log.resource_id}
                              </p>
                            )}
                            {log.details?.changes_count > 0 && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                {log.details.changes_count} field(s) changed
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-slate-500">
                            {new Date(log.created_at).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-slate-400">
                            {new Date(log.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pagination */}
                <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    Showing {offset + 1} - {offset + logs.logs.length} of {logs.total}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setOffset(Math.max(0, offset - 20))}
                      disabled={offset === 0}
                      className="px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-white dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setOffset(offset + 20)}
                      disabled={!logs.has_more}
                      className="px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-white dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No audit logs found</p>
                {(filters.resource_type || filters.action) && (
                  <button
                    onClick={() => setFilters({ resource_type: '', action: '' })}
                    className="mt-2 text-sm text-indigo-600 hover:text-indigo-700"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default AuditLogViewer;
